import React, { useState, useRef, useEffect } from 'react';
import './DeploymentsBubble.css';
import deploymentIcon from '../../assets/versioncontrol.png'; // Import the new icon

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TRANSITION_DURATION = 250; // ms

const StarIcon = ({ filled }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill={filled ? '#FFD700' : 'none'}
    stroke={filled ? '#000' : '#bbb'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ verticalAlign: 'middle', marginRight: 6 }}
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const DeploymentsBubble = ({ hasUnsavedChanges, onSaveDeployment, deployments, setDeployments, loadedId, setLoadedId, onLoadDeployment, showList, setShowList }) => {
  const [transitioning, setTransitioning] = useState(false);
  const [confirmingDeployment, setConfirmingDeployment] = useState(null); // Track which deployment is confirming
  const [customName, setCustomName] = useState('');

  const popupRef = useRef(null);

  // Click outside to close
  useEffect(() => {
    if (!showList) return;
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleHideList();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showList]);

  const handleShowList = () => {
    setConfirmingDeployment(null); // Always reset to table view
    setTransitioning(true);
    setTimeout(() => {
      setShowList(true);
      setTransitioning(false);
    }, TRANSITION_DURATION);
  };

  const handleHideList = () => {
    setTransitioning(true);
    setTimeout(() => {
      setShowList(false);
      setTransitioning(false);
      setConfirmingDeployment(null); // Always reset confirmation state when closing
    }, TRANSITION_DURATION);
  };

  const handleSetPublished = (e, dep, isActive) => {
    e.stopPropagation();
    if (!isActive) {
      setConfirmingDeployment(dep);
    }
  };

  const handleConfirmPublish = async () => {
    if (confirmingDeployment) {
      try {
        const token = sessionStorage.getItem('token');
        const res = await fetch(
          `${API_URL}/api/event-invites/template/activate/${confirmingDeployment.id}`,
          {
            method: 'PUT',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }
        );
        if (res.status === 401) {
          window.location = '/login';
          return;
        }
      } catch (err) {
        console.error('Failed to activate deployment:', err);
      }
      
      setDeployments(deployments.map(dep => ({ ...dep, active: dep.id === confirmingDeployment.id })));
    }
    setConfirmingDeployment(null);
  };

  const handleCancelPublish = () => {
    setConfirmingDeployment(null);
  };

  return (
    <div className="deployments-bubble-container">
      {!showList ? (
        <div
          className={`bouvlw-icon transition-fade${transitioning ? ' fade-out' : ' fade-in'}`}
          onClick={handleShowList}
          title="Show Deployments"
        >
          <img src={deploymentIcon} alt="Deployments" width={44} height={44} />
        </div>
      ) : (
        <div ref={popupRef} className={`window-flip-container${confirmingDeployment ? ' flipped' : ''}`} style={{ width: '100%' }}>
          <div className="window-flipper">
            <div className="window-front">
              <div className={`deployments-list transition-fade${transitioning ? ' fade-out' : ' fade-in'}`}>
                <div className="deployments-list-header">
                  <span>Saved Templates</span>
                  <span className="info-icon-wrapper superscript">
                    <span className="info-icon" tabIndex="0">
                      <svg width="14" height="14" viewBox="0 0 24 24" style={{ verticalAlign: 'middle', borderRadius: '50%' }}>
                        <circle cx="12" cy="12" r="10" fill="none" stroke="#222" strokeWidth="2" />
                        <text
                          x="12"
                          y="11.5"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="13"
                          fill="#222"
                          fontFamily="Georgia,Times,serif"
                          fontStyle="italic"
                          fontWeight="bold"
                        >i</text>
                      </svg>
                      <div className="deployments-tooltip">
                        <strong>Tip:</strong> The <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFD700" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', margin: '0 2px 2px 2px' }}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg> marks the template that is currently published.<br/>
                        Up to 10 templates are saved. If you save more, the oldest will be deleted automatically.
                      </div>
                    </span>
                  </span>
                  <button className="close-btn" onClick={handleHideList}>✕</button>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Name</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deployments.map(dep => {
                      const isLoaded = dep.id === loadedId;
                      const isActive = dep.active;
                      let rowClass = '';
                      if (isLoaded && isActive) rowClass = 'loaded-template active-template';
                      else if (isLoaded) rowClass = 'loaded-template';
                      else if (isActive) rowClass = 'active-template';
                      return (
                        <tr
                          key={dep.id}
                          className={rowClass}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setLoadedId(dep.id);
                            if (onLoadDeployment) onLoadDeployment(dep);
                          }}
                        >
                          <td>
                            {isActive ? (
                              <span
                                style={{ display: 'inline-block', cursor: 'pointer' }}
                                title="Published template"
                              >
                                <StarIcon filled={true} />
                              </span>
                            ) : isLoaded ? (
                              <span
                                style={{ display: 'inline-block', cursor: 'pointer' }}
                                onClick={e => handleSetPublished(e, dep, isActive)}
                                title="Set as published"
                              >
                                <StarIcon filled={false} />
                              </span>
                            ) : null}
                          </td>
                          <td>{dep.name}</td>
                          <td>{dep.created}</td>
                        </tr>
                      );
                    })}
                    {hasUnsavedChanges && (
                      <tr>
                        <td>
                          <button
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: customName ? 'pointer' : 'not-allowed',
                              fontSize: 20,
                              color: customName ? '#2a7' : '#aaa',
                              opacity: customName ? 1 : 0.6
                            }}
                            title="Save as new template"
                            disabled={!customName}
                            onClick={() => {
                              if (onSaveDeployment && customName) {
                                onSaveDeployment(customName);
                                setCustomName('');
                              }
                            }}
                          >
                            <span style={{ fontWeight: 'bold', fontSize: 22, lineHeight: 1 }}>+</span>
                          </button>
                        </td>
                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <input
                            type="text"
                            value={customName}
                            onChange={e => setCustomName(e.target.value)}
                            placeholder="Custom name"
                            style={{ maxWidth: '100px', width: '100%', fontSize: 15, padding: '2px 6px', borderRadius: 4, border: 'none', background: '#fff', outline: 'none' }}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className={`window-back${transitioning ? ' transition-fade fade-out' : ''}`}>
              {confirmingDeployment && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 400, marginBottom: 16, textAlign: 'center', color: '#333' }}>
                    You are about to change the live template.<br/>Are you sure you wish to continue?
                  </div>
                  <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
                    <button
                      style={{
                        background: 'linear-gradient(90deg, #ffe082 0%, #ffd54f 100%)',
                        border: '1px solid #e0b800',
                        borderRadius: 6,
                        padding: '7px 22px',
                        fontWeight: 500,
                        fontSize: 15,
                        color: '#222',
                        cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(220,180,30,0.08)',
                        transition: 'background 0.2s, box-shadow 0.2s',
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#ffe082'}
                      onMouseOut={e => e.currentTarget.style.background = 'linear-gradient(90deg, #ffe082 0%, #ffd54f 100%)'}
                      onClick={handleConfirmPublish}
                    >
                      Deploy
                    </button>
                    <button
                      style={{
                        background: '#fafafa',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        padding: '7px 22px',
                        fontWeight: 400,
                        fontSize: 15,
                        color: '#444',
                        cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                        transition: 'background 0.2s, box-shadow 0.2s',
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                      onMouseOut={e => e.currentTarget.style.background = '#fafafa'}
                      onClick={handleCancelPublish}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeploymentsBubble; 